import _defineProperty2 from "/builder/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _classCallCheck from "/builder/node_modules/@babel/runtime/helpers/esm/classCallCheck.js";
import _createClass from "/builder/node_modules/@babel/runtime/helpers/esm/createClass.js";
import _possibleConstructorReturn from "/builder/node_modules/@babel/runtime/helpers/esm/possibleConstructorReturn.js";
import _assertThisInitialized from "/builder/node_modules/@babel/runtime/helpers/esm/assertThisInitialized.js";
import _get from "/builder/node_modules/@babel/runtime/helpers/esm/get.js";
import _getPrototypeOf from "/builder/node_modules/@babel/runtime/helpers/esm/getPrototypeOf.js";
import _inherits from "/builder/node_modules/@babel/runtime/helpers/esm/inherits.js";
import _createSuper from "/builder/node_modules/@babel/runtime/helpers/esm/createSuper.js";
import _toConsumableArray from "/builder/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import { object, invariant, ErrorConstant, isPlainObject, isFunction, isRemirrorManager, RemirrorManager, getLazyArray, getMarkRange, Framework, shouldUseDomEnvironment, STATE_OVERRIDE, isArray, isNullOrUndefined, isTextSelection, PluginsExtension } from '@remirror/core';
import { corePreset } from '@remirror/preset-core';
import { ReactExtension, ReactPlaceholderExtension } from '@remirror/preset-react';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import { usePortals, RemirrorPortals } from '@remirror/extension-react-component';
import { ComponentsTheme } from '@remirror/theme';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useState, useCallback, createContext, useContext, useRef, useEffect, useMemo } from 'react';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import useLayoutEffect from 'use-isomorphic-layout-effect';
import usePrevious from 'use-previous';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _classPrivateFieldSet from '@babel/runtime/helpers/esm/classPrivateFieldSet';
import _classPrivateFieldGet from '@babel/runtime/helpers/esm/classPrivateFieldGet';
import composeRefs from '@seznam/compose-react-refs';
import { RemirrorSSR, createEditorView } from '@remirror/react-ssr';
import { addKeyToElement } from '@remirror/react-utils';
import { createContextState } from 'create-context-state';
import { i18n } from '@remirror/i18n';
import isEqual from 'fast-deep-equal/react';
import warning from 'tiny-warning';
/**
 * Returning a new object reference guarantees that a before-and-after
 * equivalence check will always be false, resulting in a re-render, even when
 * multiple calls to forceUpdate are batched.
 *
 * @internal
 */

function useForceUpdate() {
  var _useState = useState(object()),
      _useState2 = _slicedToArray(_useState, 2),
      setState = _useState2[1];

  return useCallback(function () {
    setState(object());
  }, []);
}
/**
 * The `ReactContext` for the Remirror editor.
 */


var RemirrorContext = /*#__PURE__*/createContext(null);
/**
 * This provides access to the remirror context when using the `Remirror`.
 *
 * The first argument which is optional can also be a change handler which is
 * called every time the state updates.
 *
 * @remarks
 *
 * The following example applies the root props to the div.
 *
 * ```tsx
 * import { Remirror, useRemirrorContext } from 'remirror';
 *
 * const HooksComponent = (props) => {
 *   // This pulls the remirror props out from the context.
 *   const { getRootProps } = useRemirrorContext();
 *
 *   return <div {...getRootProps()} />;
 * }
 *
 * class App extends Component {
 *   render() {
 *     return (
 *       <Remirror>
 *         <HooksComponent />
 *       </Remirror>
 *     );
 *   }
 * }
 * ```
 *
 * For performance reasons `useRemirror` does not automatically trigger a
 * rerender on every editor update. This allows for you use it in component
 * which don't need to track the latest editor state, without suffering a
 * performance penalty.
 *
 * However if you do want to track whether a command is enabled at the current
 * selection or whether a certain formatting mark (bold) is active at the
 * current selection you can pass through an optional parameter.
 *
 * ```tsx
 * const EditorButton = () => {
 *   const { active, commands } = useRemirrorContext({ autoUpdate: true });
 *
 *   return (
 *     <button style={{ fontWeight: active.bold() ? 'bold' : undefined }}>
 *       B
 *     <button>
 *   );
 * }
 * ```
 *
 * The above example keep track of whether the current selection is bold on
 * every update to the editor. Updates can be document changes and selection
 * changes.
 *
 * For more control you can also use a handler function as the first parameter
 * to selectively rerender as you see fit.
 *
 * ```tsx
 * const EditorButton = () => {
 *   const { active, commands } = useRemirrorContext(() => {
 *     if (active.bold() === boldActive) {
 *       return;
 *     }
 *
 *     setBoldActive(active.bold());
 *   });
 *
 *   const [boldActive, setBoldActive] = useState(active.bold());
 *
 *   return (
 *     <button style={{ fontWeight: boldActive ? 'bold' : undefined }}>
 *       B
 *     <button>
 *   )
 * }
 * ```
 *
 * In this case the component only re-renders when the bold formatting changes.
 */

function useRemirrorContext(handler) {
  // This is not null when rendering within the `Remirror`. The majority
  // of times this is called, this will be the case.
  var context = useContext(RemirrorContext); // A helper for forcing an update of the state.

  var forceUpdate = useRef(useForceUpdate()); // Throw an error if context doesn't exist.

  !context ? process.env.NODE_ENV !== "production" ? invariant(false, {
    code: ErrorConstant.REACT_PROVIDER_CONTEXT
  }) : invariant(false) : void 0; // Destructure the `addHandler` to minimise updates in the `useEffect` hook.

  var addHandler = context.addHandler; // Manages the update frequency of this hook.

  useEffect(function () {
    var updateHandler = handler; // Default state, do nothing.

    if (!updateHandler) {
      return;
    } // Use the `forceUpdate` handler when `autoUpdate` is true.


    if (isPlainObject(updateHandler)) {
      var _updateHandler = updateHandler,
          autoUpdate = _updateHandler.autoUpdate;
      updateHandler = autoUpdate ? function () {
        return forceUpdate.current();
      } : undefined;
    }

    if (!isFunction(updateHandler)) {
      return;
    } // Add the update handler which will update this hook every time the editor
    // is updated.


    return addHandler('updated', updateHandler);
  }, [addHandler, handler]);
  return context;
}
/**
 * This is a shorthand method for retrieving the active available in the
 * editor.
 *
 * ```ts
 * import { useActive } from '@remirror/react';
 * ```
 *
 * This hooks updates the local component on each state update for the editor,
 * so it can be quite expensive.
 *
 * @param autoUpdate - Set to false to prevent automatic re-rendering on every
 * state update.
 */


function useActive() {
  var autoUpdate = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
  return useRemirrorContext({
    autoUpdate: autoUpdate
  }).active;
}
/**
 * A core hook which provides the attributes for the nodes and marks in the
 * editor.
 *
 * ```tsx
 * import { useAttrs } from '@remirror/react';
 *
 * const EditorButton = () => {
 *   const attrs = useAttrs();
 *   const { link } = attrs;
 *
 *   return <a href={link.href}>{link().href}</a>;
 * }
 * ```
 */


function useAttrs() {
  var update = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
  return useRemirrorContext(update ? {
    autoUpdate: true
  } : undefined).attrs;
}
/**
 * A core hook which provides the chainable commands for usage in your editor.
 *
 * ```tsx
 * import { useChainedCommands } from '@remirror/react';
 *
 * const EditorButton = () => {
 *   const chain = useChainedCommands();
 *
 *   return (
 *     <>
 *       <button onClick={() => chain.toggleBold().toggleItalic().run()}>
 *         Chain me!
 *       </button>
 *     </>
 *   );
 * }
 * ````
 */


function useChainedCommands() {
  return useRemirrorContext().chain;
}
/**
 * A core hook which provides the commands for usage in your editor.
 *
 * ```tsx
 * import { useCommands } from '@remirror/react';
 *
 * const EditorButton = () => {
 *   const commands = useCommands();
 *
 *   return (
 *     <>
 *       <button onClick={() => commands.toggleBold()}>
 *         Click me!
 *       </button>
 *     </>
 *   );
 * }
 * ````
 */


function useCommands() {
  return useRemirrorContext().commands;
}
/**
 * A core hook which returns the current selection.
 */


function useCurrentSelection() {
  return useRemirrorContext({
    autoUpdate: true
  }).getState().selection;
}
/**
 * A hook which provides access to the editor view.
 */


function useEditorView() {
  return useRemirrorContext().view;
}
/**
 * A hook which provides a react ref wrapper for the `view.dom`.
 */


function useEditorDomRef() {
  return useRef(useEditorView().dom);
}
/**
 * A core hook which provides the latest editor state every time that it
 * updates.
 */


function useEditorState() {
  return useRemirrorContext({
    autoUpdate: true
  }).getState();
}
/**
 * A `useEffect` function which issues a warning when an update occurs with
 * dependencies which are deeply equal. This only happens `development`. In
 * production it aliases the `useEffect` hook.
 *
 * This is used in places where it's important for developers to memoize and
 * wrap methods with `useCallback`. Failure to do so will result in multiple
 * renders for the exact same content and slow down the editing experience.
 *
 * @internal
 */


var useEffectWithWarning = process.env.NODE_ENV === 'production' ? useEffect : function (effect, deps) {
  var firstUpdate = useRef(true);
  var ref = useRef();
  var unnecessaryChange = useRef(0);
  useEffect(function () {
    // Use the effect only the first render.
    if (firstUpdate.current) {
      firstUpdate.current = false; // Hold the initial reference for the deps.

      ref.current = deps;
      return;
    } // Only run the following on every subsequent render.


    if (!isEqual(deps, ref.current)) {
      unnecessaryChange.current = 0;
      ref.current = deps;
      return;
    }

    unnecessaryChange.current += 1;
  });

  var wrappedEffect = function wrappedEffect() {
    if (unnecessaryChange.current > 0) {
      // TODO remove this before release
      throw new Error("SERIOUS ".concat(unnecessaryChange.current));
    }

    process.env.NODE_ENV !== "production" ? warning(unnecessaryChange.current === 0, "The dependencies passed into your useEffect are identical, but an update has been triggered ".concat(unnecessaryChange.current, " time(s). Please consider wrapping the values with `useMemo` or `useCallback` to memoize your dependencies and prevent unnecessary re-renders.")) : void 0;
    return effect();
  }; // eslint-disable-next-line react-hooks/exhaustive-deps


  useEffect(wrappedEffect, deps);
};
/**
 * Dynamically update the properties of your extension via hooks. Provide the
 * Extension constructor and the properties you want to update.
 *
 * @remarks
 *
 * Please note that every time the properties change your extension is updated.
 * You will want to memoize or prevent needless updates somehow to the
 * properties passed in.
 *
 * This is only available within the context of the `Remirror` it will
 * throw an error otherwise.
 *
 * It can be used with three distinct call signatures.
 *
 * **Get the extension instance**
 *
 * ```tsx
 * import { useExtension } from '@remirror/react';
 * import { BoldExtension } from 'remirror/extensions';
 *
 * const Editor = () => {
 *   const boldExtension = useExtension(BoldExtension);
 *
 *   boldExtension.setOptions({ weight: '800' });
 *
 *   return null;
 * }
 * ```
 *
 * **Update the extension properties**
 *
 * ```tsx
 * import { useExtension } from '@remirror/react';
 * import { PlaceholderExtension } from 'remirror/extensions';
 *
 * const EditorPlaceholder = ({ placeholder = 'Your magnum opus' }) => {
 *   useExtension(PlaceholderExtension, { placeholder }); // Updates the placeholder.
 *
 *   return null;
 * }
 * ```
 *
 * **Add event handlers to your extension**
 *
 * ```tsx
 * import { useCallback } from 'react';
 * import { HistoryExtension, HistoryOptions } from 'remirror/extensions';
 * import { useExtension } from '@remirror/react';
 *
 * const Editor = ({ placeholder = 'Your magnum opus' }) => {
 *   useExtension(
 *     HistoryExtension,
 *     useCallback(
 *       ({ addHandler }) => {
 *         return addHandler('onRedo', () => log('a redo just happened'));
 *       },
 *       [],
 *     ),
 *     [event, handler],
 *   );
 *
 *   return null;
 * };
 * ```
 *
 * These hooks can serve as the building blocks when customizing your editor
 * experience with `remirror`.
 */

function useExtension(Constructor) {
  var optionsOrCallback = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : undefined;
  var dependencies = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : [];

  var _useRemirrorContext = useRemirrorContext(),
      getExtension = _useRemirrorContext.getExtension;

  var extension = useMemo(function () {
    return getExtension(Constructor);
  }, [Constructor, getExtension]);
  var deps = isFunction(optionsOrCallback) ? [extension].concat(_toConsumableArray(dependencies)) : optionsOrCallback ? [extension].concat(_toConsumableArray(Object.values(optionsOrCallback))) : []; // Handle the case where an options object is passed in.

  useEffectWithWarning(function () {
    if (isFunction(optionsOrCallback) || !optionsOrCallback) {
      return;
    }

    extension.setOptions(optionsOrCallback);
  }, deps);
  useEffectWithWarning(function () {
    if (!isFunction(optionsOrCallback)) {
      return;
    }

    return optionsOrCallback({
      addHandler: extension.addHandler.bind(extension),
      addCustomHandler: extension.addCustomHandler.bind(extension),
      extension: extension
    });
  }, deps); // Return early when more than one argument received.

  if (optionsOrCallback) {
    return;
  }

  return extension;
}
/**
 * A core hook which provides the helpers for usage in your editor.
 *
 * ```tsx
 * import { useHelpers } from '@remirror/react';
 *
 * const EditorButton = () => {
 *   const helpers = useHelpers();
 *
 *   return (
 *     <>
 *       <button onClick={() => helpers.toggleBold()}>
 *         Click me!
 *       </button>
 *       <button onClick={() => helpers.chain.toggleBold().toggleItalic().run()}>
 *         Chain me!
 *       </button>
 *     </>
 *   );
 * }
 * ````
 *
 * Passing `true` as the first argument will ensure that the component this hook
 * is placed inside will rerender on every update.
 */


function useHelpers() {
  var update = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
  return useRemirrorContext(update ? {
    autoUpdate: true
  } : undefined).helpers;
}
/**
 * A Provider and hooks component for the remirror i18n helper library.
 *
 * This uses `@lingui/core` in the background. So please star and support the
 * project when you have a moment.
 */


var _createContextState = createContextState(function (_ref) {
  var _props$locale, _props$i18n, _props$supportedLocal;

  var props = _ref.props;
  var locale = (_props$locale = props.locale) !== null && _props$locale !== void 0 ? _props$locale : 'en';
  var i18n$1 = (_props$i18n = props.i18n) !== null && _props$i18n !== void 0 ? _props$i18n : i18n;
  var supportedLocales = (_props$supportedLocal = props.supportedLocales) !== null && _props$supportedLocal !== void 0 ? _props$supportedLocal : [locale];

  var t = function t() {
    return i18n$1._.apply(i18n$1, arguments);
  };

  return {
    locale: locale,
    i18n: i18n$1,
    supportedLocales: supportedLocales,
    t: t
  };
}),
    _createContextState2 = _slicedToArray(_createContextState, 2),
    I18nProvider = _createContextState2[0],
    useI18n = _createContextState2[1];

var _excluded$3 = ["core", "react"];
/**
 * Create a React [[`RemirrorManager`]] with all the default react presets and
 * extensions.
 */

function createReactManager(extensions) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

  var core = options.core,
      react = options.react,
      settings = _objectWithoutProperties(options, _excluded$3);

  if (isRemirrorManager(extensions)) {
    return extensions;
  }

  return RemirrorManager.create(function () {
    return [].concat(_toConsumableArray(getLazyArray(extensions)), [new ReactExtension(react)], _toConsumableArray(corePreset(core)));
  }, settings);
}
/**
 * A hook for creating the editor manager directly in the react component.
 *
 * @remarks
 *
 * The manager is a singleton and doesn't rerender until `manager.destroy()` is
 * called. You should call this method in a `useEffect`
 *
 * This is intentional. However, it's something that can be addressed if it
 * causes issues.
 *
 * ```tsx
 * import { useExtension } from '@remirror/react';
 * import { PresetCore } from 'remirror/preset-core';
 * import { BoldExtension } from 'remirror/extension-bold';
 *
 * const Framework = () => {
 *   const manager = useManager([new BoldExtension(), new CorePreset()]);
 *
 *   <Remirror >
 *     <MyEditor />
 *   </Remirror>;
 * }
 * ```
 *
 * This is intended for internal usage only.
 *
 * @internal
 */


function useManager(extensions) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {}; // Store the value in refs so that they can be used in the `useEffect` hook
  // without being passed as dependencies.

  var extensionsRef = useRef(extensions);
  var optionsRef = useRef(options);

  var _useState = useState(function () {
    return createReactManager(extensions, options);
  }),
      _useState2 = _slicedToArray(_useState, 2),
      manager = _useState2[0],
      setManager = _useState2[1]; // Keep the parameter refs up to date with the latest value.


  extensionsRef.current = extensions;
  optionsRef.current = options; // Add a manager destroyed listener to only cleanup the manager when it is
  // destroyed.

  useEffect(function () {
    return manager.addHandler('destroy', function () {
      // `clone` is used to ensure that that any stale extensions are
      // reinitialized.
      setManager(function () {
        return createReactManager(extensionsRef.current, optionsRef.current);
      });
    });
  }, [manager]);
  return manager;
}
/**
 * A hook which returns the selected range of the mark of the provided type.
 */


function useMarkRange(type) {
  var _useRemirrorContext = useRemirrorContext({
    autoUpdate: true
  }),
      getState = _useRemirrorContext.getState;

  var _getState$selection = getState().selection,
      $from = _getState$selection.$from,
      $to = _getState$selection.$to;
  return getMarkRange($from, type, $to);
}
/**
 * A hook which provides access to the portal container for rendering react
 * component directly within the editor dom.
 */


function usePortalContainer() {
  return useRemirrorContext().portalContainer;
}

function _checkPrivateRedeclaration(obj, privateCollection) {
  if (privateCollection.has(obj)) {
    throw new TypeError("Cannot initialize the same private elements twice on an object");
  }
}

function _classPrivateFieldInitSpec(obj, privateMap, value) {
  _checkPrivateRedeclaration(obj, privateMap);

  privateMap.set(obj, value);
}

var _excluded$2 = ["refKey", "ref"],
    _excluded2 = ["state"];

var _getShouldRenderClient = /*#__PURE__*/new WeakMap();

var _setShouldRenderClient = /*#__PURE__*/new WeakMap();

var _editorRef = /*#__PURE__*/new WeakMap();

var ReactFramework = /*#__PURE__*/function (_Framework) {
  _inherits(ReactFramework, _Framework);

  var _super = _createSuper(ReactFramework);

  function ReactFramework(props) {
    var _this;

    _classCallCheck(this, ReactFramework);

    var _this$props$placehold;

    _this = _super.call(this, props);

    _classPrivateFieldInitSpec(_assertThisInitialized(_this), _getShouldRenderClient, {
      writable: true,
      value: void 0
    });

    _classPrivateFieldInitSpec(_assertThisInitialized(_this), _setShouldRenderClient, {
      writable: true,
      value: void 0
    });

    _classPrivateFieldInitSpec(_assertThisInitialized(_this), _editorRef, {
      writable: true,
      value: void 0
    });

    _defineProperty(_assertThisInitialized(_this), "rootPropsConfig", {
      called: false,
      count: 0
    });

    _defineProperty(_assertThisInitialized(_this), "getRootProps", function (options) {
      return _this.internalGetRootProps(options, null);
    });

    _defineProperty(_assertThisInitialized(_this), "internalGetRootProps", function (options, children) {
      var _objectSpread2;

      // Ensure that this is the first time `getRootProps` is being called during
      // this commit phase of the .
      // invariant(!this.rootPropsConfig.called, { code: ErrorConstant.REACT_GET_ROOT_PROPS });
      _this.rootPropsConfig.called = true;

      var _ref = options !== null && options !== void 0 ? options : object(),
          _ref$refKey = _ref.refKey,
          refKey = _ref$refKey === void 0 ? 'ref' : _ref$refKey,
          ref = _ref.ref,
          config = _objectWithoutProperties(_ref, _excluded$2);

      return _objectSpread(_objectSpread((_objectSpread2 = {}, _defineProperty2(_objectSpread2, refKey, composeRefs(ref, _this.onRef)), _defineProperty2(_objectSpread2, "key", _this.uid), _objectSpread2), config), {}, {
        children: _this.renderChildren(children)
      });
    });

    _defineProperty(_assertThisInitialized(_this), "onRef", function (element) {
      if (!element) {
        return;
      }

      _this.rootPropsConfig.count += 1;
      !(_this.rootPropsConfig.count <= 1) ? process.env.NODE_ENV !== "production" ? invariant(false, {
        code: ErrorConstant.REACT_GET_ROOT_PROPS,
        message: "Called ".concat(_this.rootPropsConfig.count, " times")
      }) : invariant(false) : void 0;

      _classPrivateFieldSet(_assertThisInitialized(_this), _editorRef, element);

      _this.onRefLoad();
    });

    _defineProperty(_assertThisInitialized(_this), "renderSsr", function () {
      var _this$props = _this.props,
          suppressHydrationWarning = _this$props.suppressHydrationWarning,
          editable = _this$props.editable;

      if (shouldUseDomEnvironment(_this.manager.settings.forceEnvironment) && (!suppressHydrationWarning || _this.shouldRenderClient)) {
        return null;
      }

      return /*#__PURE__*/jsx(RemirrorSSR, {
        attributes: _this.getAttributes(true),
        state: _this.getState(),
        manager: _this.manager,
        editable: editable !== null && editable !== void 0 ? editable : true
      });
    });

    var getShouldRenderClient = props.getShouldRenderClient,
        setShouldRenderClient = props.setShouldRenderClient;

    _classPrivateFieldSet(_assertThisInitialized(_this), _getShouldRenderClient, getShouldRenderClient);

    _classPrivateFieldSet(_assertThisInitialized(_this), _setShouldRenderClient, setShouldRenderClient);

    if (_this.manager.view) {
      _this.manager.view.setProps({
        state: _this.manager.view.state,
        dispatchTransaction: _this.dispatchTransaction,
        attributes: function attributes() {
          return _this.getAttributes();
        },
        editable: function editable() {
          var _this$props$editable;

          return (_this$props$editable = _this.props.editable) !== null && _this$props$editable !== void 0 ? _this$props$editable : true;
        }
      });

      return _possibleConstructorReturn(_this);
    }

    _this.manager.getExtension(ReactPlaceholderExtension).setOptions({
      placeholder: (_this$props$placehold = _this.props.placeholder) !== null && _this$props$placehold !== void 0 ? _this$props$placehold : ''
    });

    return _this;
  }
  /**
   * This is called to update props on every render so that values don't become stale.
   */


  _createClass(ReactFramework, [{
    key: "shouldRenderClient",
    get:
    /**
     * Whether to render the client immediately.
     */

    /**
     * Update the should render client state input.
     */

    /**
     * Stores the Prosemirror EditorView dom element.
     */

    /**
     * Used when suppressHydrationWarning is true to determine when it's okay to
     * render the client content.
     */
    function get() {
      return _classPrivateFieldGet(this, _getShouldRenderClient).call(this);
    }
    /**
     * Keep track of whether the get root props has been called during the most recent render.
     */

  }, {
    key: "name",
    get: function get() {
      return 'react';
    }
  }, {
    key: "update",
    value: function update(props) {
      _get(_getPrototypeOf(ReactFramework.prototype), "update", this).call(this, props);

      var getShouldRenderClient = props.getShouldRenderClient,
          setShouldRenderClient = props.setShouldRenderClient;

      _classPrivateFieldSet(this, _getShouldRenderClient, getShouldRenderClient);

      _classPrivateFieldSet(this, _setShouldRenderClient, setShouldRenderClient);

      return this;
    }
    /**
     * Create the prosemirror editor view.
     */

  }, {
    key: "createView",
    value: function createView(state) {
      var _this2 = this;

      return createEditorView(undefined, {
        state: state,
        dispatchTransaction: this.dispatchTransaction,
        attributes: function attributes() {
          return _this2.getAttributes();
        },
        editable: function editable() {
          var _this$props$editable2;

          return (_this$props$editable2 = _this2.props.editable) !== null && _this$props$editable2 !== void 0 ? _this$props$editable2 : true;
        }
      }, this.manager.settings.forceEnvironment);
    }
    /**
     * The external `getRootProps` that is used to spread props onto a desired
     * holder element for the prosemirror view.
     */

    /**
     * Updates the state either by calling `onChange` when it exists or
     * directly setting the internal state via a `setState` call.
     */

  }, {
    key: "updateState",
    value: function updateState(_ref2) {
      var state = _ref2.state,
          rest = _objectWithoutProperties(_ref2, _excluded2);

      var _rest$triggerChange = rest.triggerChange,
          triggerChange = _rest$triggerChange === void 0 ? true : _rest$triggerChange,
          tr = rest.tr,
          transactions = rest.transactions;

      if (this.props.state) {
        var onChange = this.props.onChange;
        !onChange ? process.env.NODE_ENV !== "production" ? invariant(false, {
          code: ErrorConstant.REACT_CONTROLLED,
          message: 'You are required to provide the `onChange` handler when creating a controlled editor.'
        }) : invariant(false) : void 0;
        !triggerChange ? process.env.NODE_ENV !== "production" ? invariant(false, {
          code: ErrorConstant.REACT_CONTROLLED,
          message: 'Controlled editors do not support `clearContent` or `setContent` where `triggerChange` is `true`. Update the `state` prop instead.'
        }) : invariant(false) : void 0;

        if (!this.previousStateOverride) {
          this.previousStateOverride = this.getState();
        }

        this.onChange({
          state: state,
          tr: tr
        });
        return;
      } // Check if this is a fresh update directly applied by the developer (without
      // transactions or commands).


      if (!tr && !transactions) {
        state = state.apply(state.tr.setMeta(STATE_OVERRIDE, {}));
      } // Update the internal prosemirror state. This happens before we update
      // the component's copy of the state.


      this.view.updateState(state);

      if (triggerChange) {
        // Update the `onChange` handler before notifying the manager but only when a change should be triggered.
        this.onChange({
          state: state,
          tr: tr
        });
      }

      this.manager.onStateUpdate({
        previousState: this.previousState,
        state: state,
        tr: tr,
        transactions: transactions
      });
    }
    /**
     * Update the controlled state when the value changes and notify the extension
     * of this update.
     */

  }, {
    key: "updateControlledState",
    value: function updateControlledState(state, previousState) {
      this.previousStateOverride = previousState; // Mark this as a state override so that extensions and plugins know to
      // ignore the transaction.

      state = state.apply(state.tr.setMeta(STATE_OVERRIDE, {}));
      this.view.updateState(state);
      this.manager.onStateUpdate({
        previousState: this.previousState,
        state: state
      });
      this.previousStateOverride = undefined;
    }
    /**
     * Adds the prosemirror view to the dom in the position specified via the
     * component props.
     */

  }, {
    key: "addProsemirrorViewToDom",
    value: function addProsemirrorViewToDom(element, viewDom) {
      if (this.props.insertPosition === 'start') {
        element.insertBefore(viewDom, element.firstChild);
      } else {
        element.append(viewDom);
      }
    }
    /**
     * Called once the container dom node (`this.editorRef`) has been initialized
     * after the component mounts.
     *
     * This method handles the cases where the dom is not focused.
     */

  }, {
    key: "onRefLoad",
    value: function onRefLoad() {
      !_classPrivateFieldGet(this, _editorRef) ? process.env.NODE_ENV !== "production" ? invariant(false, {
        code: ErrorConstant.REACT_EDITOR_VIEW,
        message: 'Something went wrong when initializing the text editor. Please check your setup.'
      }) : invariant(false) : void 0;
      var autoFocus = this.props.autoFocus;
      this.addProsemirrorViewToDom(_classPrivateFieldGet(this, _editorRef), this.view.dom);

      if (autoFocus) {
        this.focus(autoFocus);
      }

      this.onChange();
      this.addFocusListeners();
    }
  }, {
    key: "onMount",
    value: function onMount() {
      var suppressHydrationWarning = this.props.suppressHydrationWarning;

      if (suppressHydrationWarning) {
        _classPrivateFieldGet(this, _setShouldRenderClient).call(this, true);
      }
    }
    /**
     * Called for every update of the props and state.
     */

  }, {
    key: "onUpdate",
    value: function onUpdate() {
      var _this3 = this;

      // Ensure that `children` is still a render prop
      // propIsFunction(this.props.children);
      // Check whether the editable prop has been updated
      if (this.view && _classPrivateFieldGet(this, _editorRef)) {
        this.view.setProps(_objectSpread(_objectSpread({}, this.view.props), {}, {
          editable: function editable() {
            var _this$props$editable3;

            return (_this$props$editable3 = _this3.props.editable) !== null && _this$props$editable3 !== void 0 ? _this$props$editable3 : true;
          }
        }));
      }
    }
    /**
     * Get the framework output.
     */

  }, {
    key: "frameworkOutput",
    get: function get() {
      return _objectSpread(_objectSpread({}, this.baseOutput), {}, {
        getRootProps: this.getRootProps,
        portalContainer: this.manager.store.portalContainer,
        renderSsr: this.renderSsr
      });
    }
    /**
     * Checks whether this is an SSR environment and returns a child array with
     * the SSR component
     *
     * @param children
     *
     * TODO - this is useless and should be refactored.
     */

  }, {
    key: "renderChildren",
    value: function renderChildren() {
      var child = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
      var _this$props2 = this.props,
          _this$props2$insertPo = _this$props2.insertPosition,
          insertPosition = _this$props2$insertPo === void 0 ? 'end' : _this$props2$insertPo,
          suppressHydrationWarning = _this$props2.suppressHydrationWarning;
      var children = isArray(child) ? child : [child];

      if (shouldUseDomEnvironment(this.manager.settings.forceEnvironment) && (!suppressHydrationWarning || this.shouldRenderClient)) {
        return children;
      }

      var ssrElement = this.renderSsr();
      return (insertPosition === 'start' ? [ssrElement].concat(_toConsumableArray(children)) : [].concat(_toConsumableArray(children), [ssrElement])).map(addKeyToElement);
    }
    /**
     * Return a JSX Element to be used within the ssr rendering phase.
     */

    /**
     * Reset the called status of `getRootProps`.
     */

  }, {
    key: "resetRender",
    value: function resetRender() {
      // Reset the status of roots props being called
      this.rootPropsConfig.called = false;
      this.rootPropsConfig.count = 0;
    }
  }]);

  return ReactFramework;
}(Framework);
/**
 * The hook responsible for providing the editor context when the `Remirror`
 * component is combined with `useRemirror`.
 *
 * @remarks
 *
 * This is an internal hook which returns the `ReactFramework` instance for
 * consumption by the public API's. It is used within `useRemirror` and the
 * `<Remirror />` component.
 *
 * @internal
 */


function useReactFramework(props) {
  var _props$initialContent;

  var manager = props.manager,
      state = props.state;
  var placeholder = props.placeholder,
      editable = props.editable,
      suppressHydrationWarning = props.suppressHydrationWarning;
  var firstUpdate = useRef(true); // Update the placeholder on first render.

  if (firstUpdate.current && !isNullOrUndefined(placeholder)) {
    manager.getExtension(ReactExtension).setOptions({
      placeholder: placeholder
    });
  } // Keep the placeholder updated


  useEffect(function () {
    manager.getExtension(ReactExtension).setOptions({
      placeholder: placeholder
    });
  }, [placeholder, manager]);
  var fallback = manager.createEmptyDoc();

  var _ref = isArray(props.initialContent) ? props.initialContent : [(_props$initialContent = props.initialContent) !== null && _props$initialContent !== void 0 ? _props$initialContent : fallback],
      _ref2 = _slicedToArray(_ref, 2),
      initialContent = _ref2[0],
      initialSelection = _ref2[1];

  var initialEditorState = state ? state : manager.createState({
    content: initialContent,
    selection: initialSelection
  });

  var _useState = useState(suppressHydrationWarning ? false : undefined),
      _useState2 = _slicedToArray(_useState, 2),
      shouldRenderClient = _useState2[0],
      setShouldRenderClient = _useState2[1]; // Create the framework which manages the connection between the `@remirror/core`
  // and React.


  var framework = useFramework({
    initialEditorState: initialEditorState,
    setShouldRenderClient: setShouldRenderClient,
    getProps: function getProps() {
      return props;
    },
    getShouldRenderClient: function getShouldRenderClient() {
      return shouldRenderClient;
    }
  });
  useEffect(function () {
    framework.onMount();
    return function () {
      framework.destroy();
    };
  }, [framework]); // Handle editor updates

  useEffect(function () {
    framework.onUpdate();
  }, [editable, framework]); // Handle the controlled editor usage.

  useControlledEditor(framework);
  return framework.frameworkOutput;
}
/**
 * A hook which creates a reference to the `ReactFramework` and updates the
 * parameters on every render.
 */


function useFramework(props) {
  var propsRef = useRef(props);
  propsRef.current = props;
  var framework = useMemo(function () {
    return new ReactFramework(propsRef.current);
  }, []);
  framework.update(props);
  return framework;
}
/**
 * A hook which manages the controlled updates for the editor.
 */


function useControlledEditor(framework) {
  var state = framework.props.state; // Cache whether this is a controlled editor.

  var isControlledRef = useRef(!!state);
  var previousValue = usePrevious(state); // Using layout effect for synchronous updates.

  useLayoutEffect(function () {
    // Check if the update is valid based on current value.
    var validUpdate = state ? isControlledRef.current === true : isControlledRef.current === false;
    !validUpdate ? process.env.NODE_ENV !== "production" ? invariant(false, {
      code: ErrorConstant.REACT_CONTROLLED,
      message: isControlledRef.current ? 'You have attempted to switch from a controlled to an uncontrolled editor. Once you set up an editor as a controlled editor it must always provide a `state` prop.' : 'You have provided a `state` prop to an uncontrolled editor. In order to set up your editor as controlled you must provide the `state` prop from the very first render.'
    }) : invariant(false) : void 0;

    if (!state || state === previousValue) {
      return;
    }

    framework.updateControlledState(state, previousValue !== null && previousValue !== void 0 ? previousValue : undefined);
  }, [state, previousValue, framework]);
}

var _excluded$1 = ["content", "document", "selection", "extensions"];
/**
 * Props which are passed into the `useRemirror` hook.
 */

/**
 * A hook which replaces the [[`Remirror`]] and gives you total control of
 * the editor you can create.
 *
 * This is very experimental and if successful could replace the current
 * patterns being used.
 */

function useRemirror() {
  var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var content = props.content;
  props.document;

  var selection = props.selection,
      extensions = props.extensions,
      settings = _objectWithoutProperties(props, _excluded$1);

  var manager = useManager(extensions !== null && extensions !== void 0 ? extensions : function () {
    return [];
  }, settings);

  var _useState = useState(function () {
    return manager.createState({
      selection: selection,
      content: content !== null && content !== void 0 ? content : manager.createEmptyDoc()
    });
  }),
      _useState2 = _slicedToArray(_useState, 2),
      state = _useState2[0],
      setState = _useState2[1];

  var onChange = useCallback(function (_ref) {
    var state = _ref.state;
    setState(state);
  }, []);
  var getContext = useCallback(function () {
    var context = manager.output;
    return context;
  }, [manager]); // Memoize the return to prevent unnecessary re-renders when props change.

  return useMemo(function () {
    return {
      state: state,
      setState: setState,
      manager: manager,
      onChange: onChange,
      getContext: getContext
    };
  }, [getContext, manager, onChange, state]);
}
/**
 * A core hook which provides the the currently selected text.
 *
 * ```tsx
 * import { useSelectedText } from '@remirror/react';
 *
 * const RandomSpan = () => {
 *   const text = useSelectedText();
 *
 *   return text  && <span>{text}</span>;
 * }
 * ````
 *
 * Return the value of the currently selected text. When the text selection is empty
 */


function useSelectedText() {
  var _useRemirrorContext = useRemirrorContext({
    autoUpdate: true
  }),
      getState = _useRemirrorContext.getState,
      helpers = _useRemirrorContext.helpers;

  var _getState = getState(),
      selection = _getState.selection,
      doc = _getState.doc;

  if (!isTextSelection(selection) || selection.empty) {
    return;
  }

  var from = selection.from,
      to = selection.to;
  return helpers.getTextBetween(from, to, doc);
}

var noReason = {
  doc: false,
  selection: false,
  storedMark: false
};
/**
 * Provide the reason for the latest state update with boolean flags.
 */

function useUpdateReason() {
  var _useState = useState(noReason),
      _useState2 = _slicedToArray(_useState, 2),
      updateReason = _useState2[0],
      setUpdateReason = _useState2[1]; // Attach the editor state handler to the instance of the remirror editor.


  useExtension(PluginsExtension, function (p) {
    return p.addHandler('applyState', function (_ref) {
      var tr = _ref.tr;

      var reason = _objectSpread({}, noReason);

      if (tr.docChanged) {
        reason.doc = true;
      }

      if (tr.selectionSet) {
        reason.selection = true;
      }

      if (tr.storedMarksSet) {
        reason.storedMark = true;
      }

      setUpdateReason(reason);
    });
  }, []);
  return updateReason;
}

var _excluded = ["children", "autoRender", "i18n", "locale", "supportedLocales", "hooks"];
/**
 * The default editor placeholder where the prosemirror editor will be rendered.
 */

var EditorComponent = function EditorComponent() {
  return /*#__PURE__*/jsx("div", _objectSpread({
    className: ComponentsTheme.EDITOR_WRAPPER
  }, useRemirrorContext().getRootProps()));
};
/**
 * A component which exists to call a prop-less hook.
 */


var HookComponent = function HookComponent(props) {
  props.hook();
  return null;
};
/**
 * [[`Remirror`]] is the component for putting the editor into into it's child
 * component.
 *
 * @remarks
 *
 * The main component for remirror. This acts both as a Provider of the remirror
 * context. All components rendered within `Remirror` have access to the
 * remirror context via `useRemirrorContext`.
 *
 * I can also be rendered as a standalone editor without children. In this case
 * the context can be accessed from outside the editor via
 * `useRemirror().getContext()`.
 */


function Remirror(props) {
  var children = props.children,
      autoRender = props.autoRender,
      i18n = props.i18n,
      locale = props.locale,
      supportedLocales = props.supportedLocales,
      _props$hooks = props.hooks,
      hooks = _props$hooks === void 0 ? [] : _props$hooks,
      frameworkProps = _objectWithoutProperties(props, _excluded);

  var context = useReactFramework(frameworkProps); // Subscribe to updates from the [[`PortalContainer`]]

  var portals = usePortals(context.portalContainer); // A boolean flag which is true when a default editor should be rendered
  // first. If no children are provided and no configuration is provided for
  // autoRender, the editor will automatically be rendered.

  var autoRenderAtStart = autoRender === 'start' || autoRender === true || !children && isNullOrUndefined(autoRender); // Whether to render the editor at the end of the editor.

  var autoRenderAtEnd = autoRender === 'end';
  return /*#__PURE__*/jsx(I18nProvider, {
    i18n: i18n,
    locale: locale,
    supportedLocales: supportedLocales,
    children: /*#__PURE__*/jsxs(RemirrorContext.Provider, {
      value: context,
      children: [/*#__PURE__*/jsx(RemirrorPortals, {
        portals: portals
      }), hooks.map(function (hook, index) {
        return /*#__PURE__*/jsx(HookComponent, {
          hook: hook
        }, index);
      }), autoRenderAtStart && /*#__PURE__*/jsx(EditorComponent, {}), children, autoRenderAtEnd && /*#__PURE__*/jsx(EditorComponent, {})]
    })
  });
}

export { EditorComponent, I18nProvider, Remirror, RemirrorContext, createReactManager, useActive, useAttrs, useChainedCommands, useCommands, useCurrentSelection, useEditorDomRef, useEditorState, useEditorView, useEffectWithWarning, useExtension, useForceUpdate, useHelpers, useI18n, useManager, useMarkRange, usePortalContainer, useRemirror, useRemirrorContext, useSelectedText, useUpdateReason };